import { LitElement, html, css } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import '@spectrum-web-components/picker/sp-picker.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import { ROUTES } from './routes.js';

// The renderer action can't tell from a path alone whether it's a product
// or category page (see USF-3825 discussion), so the page type is a manual
// choice rather than something inferred from the path.
const PAGE_TYPE = Object.freeze({
    PDP: 'pdp',
    PLP: 'plp',
});

export class RendererTab extends LitElement {
    static properties = {
        contextManager: { type: Object },
        activeContextId: { type: String },
        path: { state: true },
        pageType: { state: true },
        renderedMarkup: { state: true },
        jsonLdData: { state: true },
        metaTags: { state: true },
        isLoading: { state: true },
        selectedSubTab: { state: true },
        prismLoaded: { state: true },
        errorMessage: { type: String },
        queryParams: { type: Object },
    };

    static styles = css`
        :host {
            display: block;
            padding: 20px;
        }

        .renderer-container {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .input-row {
            display: flex;
            flex-direction: row;
            gap: 16px;
            align-items: flex-end;
        }

        .input-column {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        sp-textfield {
            flex: 1;
        }

        .code-container {
            background-color: #2d2d2d;
            color: #ccc;
            padding: 16px;
            border-radius: 4px;
            margin-top: 16px;
            overflow-x: auto;
            border: 1px solid #444;
        }

        .meta-table-container {
            margin-top: 16px;
            width: 100%;
            max-width: 100%;
            word-wrap: break-word;
            font-family: 'Consolas', 'Monaco', 'Lucida Console', monospace;
            font-size: 14px;
            line-height: 1.5;
        }

        .code-container pre[class*="language-"],
        .code-container code[class*="language-"] {
            white-space: pre-wrap !important;
            word-break: break-all !important;
            word-wrap: break-word !important;
        }

        .code-container pre[class*="language-"] {
            overflow-x: auto !important;
            margin: 0;
        }

        .code-container pre,
        .code-container code {
            margin: 0;
            padding: 0;
            background: transparent;
            font-family: 'Consolas', 'Monaco', 'Lucida Console', monospace;
            font-size: 14px;
            line-height: 1.5;
            white-space: pre-wrap;
            word-break: break-all;
        }

        .loading-container {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 200px;
        }

        .loading-hidden {
            display: none;
        }

        .no-context-message {
            text-align: center;
            padding: 40px;
            background-color: var(--spectrum-gray-100);
            border-radius: 8px;
            border: 1px solid var(--spectrum-gray-300);
        }
    `;

    constructor() {
        super();
        this.contextManager = null;
        this.activeContextId = null;
        this.path = '';
        this.pageType = PAGE_TYPE.PDP;
        this.renderedMarkup = '';
        this.jsonLdData = [];
        this.metaTags = [];
        this.selectedSubTab = '1';
        this.prismLoaded = false;
        this.isLoading = false;
        this.errorMessage = '';
        this.queryParams = {};
    }

    connectedCallback() {
        super.connectedCallback();
        this.loadPrism();
    }

    updated(changedProperties) {
        super.updated(changedProperties);
        
        if (changedProperties.has('queryParams')) {
            this.processQueryParams();
        }

        if (changedProperties.has('activeContextId') && this.activeContextId) {
            if (this.path && this.path === this.queryParams.path) {
                this.handleRender();
            }
        }

        // Trigger highlighting when content changes and Prism is ready
        if ((changedProperties.has('renderedMarkup') || changedProperties.has('jsonLdData')) && this.prismLoaded) {
            console.log('Content changed, scheduling highlight');
            this.scheduleHighlight();
        }
    }

    scheduleHighlight() {
        // Use requestAnimationFrame to ensure DOM is updated
        requestAnimationFrame(() => {
            if (window.Prism && window.Prism.highlightAll) {
                window.Prism.highlightAll();
            }
        });
    }

    processQueryParams() {
        const path = this.queryParams.path;
        const contextId = this.queryParams.contextId;

        if (!contextId && !path) {
            this.path = '';
            this.pageType = PAGE_TYPE.PDP;
            this.renderedMarkup = '';
            this.jsonLdData = [];
            this.metaTags = [];
            this.errorMessage = '';
            return;
        }

        if (this.queryParams.pageType === PAGE_TYPE.PLP) {
            this.pageType = PAGE_TYPE.PLP;
        } else {
            this.pageType = PAGE_TYPE.PDP;
        }

        if (contextId) {
            this.handleContextualRenderRequest(contextId, path);
        } else if (path && path !== this.path) {
            this.path = path;
            if (this.activeContextId) {
                this.handleRender();
            }
        }
    }

    handleContextualRenderRequest(contextId, path) {
        const potentialContextId = decodeURIComponent(contextId);
        const contextExists = this.contextManager.contexts.some(ctx => ctx.id === potentialContextId);

        if (contextExists) {
            if (path) {
                this.path = path;
            }

            if (this.activeContextId !== potentialContextId) {
                this.dispatchEvent(new CustomEvent('set-active-context', {
                    bubbles: true,
                    composed: true,
                    detail: { contextId: potentialContextId }
                }));
            } else if (this.path) {
                this.handleRender();
            }
        } else {
            this.errorMessage = `Context '${potentialContextId}' not found.`;
        }
    }
    
    handlePathInput(e) {
        this.path = e.target.value;
    }

    handlePageTypeChange(e) {
        this.pageType = e.target.value;
    }

    handleRenderClick() {
        if (!this.path) {
            return;
        }
        const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
        params.set('path', this.path);
        params.set('pageType', this.pageType);
        if (this.activeContextId) {
            params.set('contextId', this.activeContextId);
        }
        window.location.hash = `#/${ROUTES.RENDERER}?v=1&${params.toString()}`;
    }

    getActiveCredentials() {
        return this.contextManager?.getContextCredentials();
    }

    async handleRender() {
        if (!this.path) {
            this.errorMessage = 'Path is required.';
            return;
        }
        const credentials = this.getActiveCredentials();
        if (!credentials) {
            console.error('No active context credentials');
            return;
        }

        const { aioNamespace } = credentials;

        if (!aioNamespace || !this.path) {
            console.error('Missing required fields');
            return;
        }

        this.isLoading = true;

        try {
            const actionBase = `https://${aioNamespace}.adobeioruntime.net/api/v1/web/aem-commerce-ssg`;
            const renderUrl = this.pageType === PAGE_TYPE.PLP
                // plp-renderer takes the category slug as a query param (no leading slash),
                // unlike pdp-renderer which parses sku/urlKey out of the URL path itself.
                ? `${actionBase}/plp-renderer?slug=${encodeURIComponent(this.path.replace(/^\//, ''))}`
                : `${actionBase}/pdp-renderer${this.path}`;

            const response = await fetch(
                renderUrl,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            const markup = await response.text();
            this.renderedMarkup = markup;

            // Parse the markup to extract meta tags and JSON-LD
            this.parseMarkup(markup);

            // Trigger syntax highlighting after content is set and DOM is updated
            this.updateComplete.then(() => {
                if (this.prismLoaded) {
                    console.log('Content updated, triggering syntax highlighting');
                    this.scheduleHighlight();
                }
            });

        } catch (error) {
            console.error('Error fetching rendered markup:', error);
            this.renderedMarkup = 'Error fetching markup';
        } finally {
            this.isLoading = false;
        }
    }

    parseMarkup(markup) {
        // Create a temporary DOM element to parse the markup
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = markup;

        // Extract meta tags
        const metaTags = [];
        const metaElements = tempDiv.querySelectorAll('meta');
        metaElements.forEach(meta => {
            const metaObj = {};
            for (const attr of meta.attributes) {
                metaObj[attr.name] = attr.value;
            }
            metaTags.push(metaObj);
        });
        this.metaTags = metaTags;

        // Extract JSON-LD data
        const jsonLdElements = tempDiv.querySelectorAll('script[type="application/ld+json"]');
        const jsonLdData = [];
        jsonLdElements.forEach(script => {
            try {
                const data = JSON.parse(script.textContent);
                jsonLdData.push(data);
            } catch (e) {
                console.error('Error parsing JSON-LD:', e);
            }
        });
        this.jsonLdData = jsonLdData;
    }

    handleSubTabChange(event) {
        this.selectedSubTab = event.target.selected;
        // Re-highlight when switching tabs
        if (this.prismLoaded) {
            this.scheduleHighlight();
        }
    }

    loadPrism() {
        // Check if Prism is already available globally
        if (window.Prism && window.Prism.languages) {
            console.log('Prism already available globally');
            this.prismLoaded = true;
            this.requestUpdate();
            return;
        }

        // Don't load if already loading
        if (document.querySelector('script[data-prism-loading]')) {
            console.log('Prism already loading');
            return;
        }

        console.log('Loading Prism...');

        // Load CSS first
        if (!document.querySelector('link[href*="prism-tomorrow"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism-tomorrow.min.css';
            document.head.appendChild(link);
            console.log('Prism CSS loaded');
        }

        // Load Prism core
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/prism.min.js';
        script.setAttribute('data-prism-loading', 'true');
        script.onload = () => {
            console.log('Prism core loaded');
            this.loadPrismComponents();
        };
        script.onerror = () => {
            console.error('Failed to load Prism core');
        };
        document.head.appendChild(script);
    }

    loadPrismComponents() {
        // Load markup component first
        const markupScript = document.createElement('script');
        markupScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/components/prism-markup.min.js';
        markupScript.onload = () => {
            console.log('Prism markup component loaded');
            
            // Then load JSON component
            const jsonScript = document.createElement('script');
            jsonScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/components/prism-json.min.js';
            jsonScript.onload = () => {
                console.log('Prism JSON component loaded');
                console.log('Available languages:', Object.keys(window.Prism.languages || {}));
                
                this.prismLoaded = true;
                this.requestUpdate();
                
                // Only highlight if we already have content
                if (this.renderedMarkup) {
                    console.log('Prism loaded and content available, highlighting');
                    setTimeout(() => {
                        this.scheduleHighlight();
                    }, 100);
                }
            };
            jsonScript.onerror = () => {
                console.error('Failed to load Prism JSON component');
            };
            document.head.appendChild(jsonScript);
        };
        markupScript.onerror = () => {
            console.error('Failed to load Prism markup component');
        };
        document.head.appendChild(markupScript);
    }

    scheduleHighlight() {
        console.log('Scheduling highlight...');
        requestAnimationFrame(() => {
            if (window.Prism && window.Prism.highlightAll) {
                console.log('Running Prism.highlightAll()');
                window.Prism.highlightAll();
            } else {
                console.warn('Prism.highlightAll not available');
            }
        });
    }

    getHighlightedCode(code, language) {
        console.log('getHighlightedCode called:', {
            prismLoaded: this.prismLoaded,
            windowPrism: !!window.Prism,
            languages: !!window.Prism?.languages,
            language: language
        });

        if (!this.prismLoaded || !window.Prism || !window.Prism.languages) {
            console.log('Prism not ready, returning plain code');
            return code;
        }

        const grammar = window.Prism.languages[language] || 
                       window.Prism.languages.markup || 
                       window.Prism.languages.html;
        
        if (!grammar) {
            console.log('No grammar found for', language, 'Available:', Object.keys(window.Prism.languages));
            return code;
        }

        try {
            const highlighted = window.Prism.highlight(code, grammar, language);
            console.log('Successfully highlighted', language, 'code');
            return highlighted;
        } catch (error) {
            console.error('Error highlighting code:', error);
            return code;
        }
    }

    renderMetaTable() {
        if (this.metaTags.length === 0) {
            return html`<p>No meta tags found.</p>`;
        }

        return html`
            <sp-table>
                <sp-table-head>
                    <sp-table-head-cell>Name</sp-table-head-cell>
                    <sp-table-head-cell>Content/Value</sp-table-head-cell>
                </sp-table-head>
                <sp-table-body>
                    ${this.metaTags.map(meta => {
                        // Get the name/property and content/value from the meta tag
                        const name = meta.name || meta.property || meta['http-equiv'] || 'Unknown';
                        const value = meta.content || meta.value || 'No content';
                        
                        return html`
                            <sp-table-row>
                                <sp-table-cell>${name}</sp-table-cell>
                                <sp-table-cell>${value}</sp-table-cell>
                            </sp-table-row>
                        `;
                    })}
                </sp-table-body>
            </sp-table>
        `;
    }

    render() {
        const credentials = this.getActiveCredentials();
        
        if (!credentials) {
            return html`
                <div class="no-context-message">
                    <h3>No Context Selected</h3>
                    <p>Please select a context from the dropdown above or create a new one in the Settings tab.</p>
                </div>
            `;
        }

        return html`
            <div class="renderer-container">
                <div class="input-row">
                    <sp-picker
                        label="Page Type"
                        @change=${this.handlePageTypeChange}
                        .value=${this.pageType}
                    >
                        <sp-menu-item value=${PAGE_TYPE.PDP}>Product (PDP)</sp-menu-item>
                        <sp-menu-item value=${PAGE_TYPE.PLP}>Category (PLP)</sp-menu-item>
                    </sp-picker>
                    <sp-textfield
                        label="Path"
                        placeholder="e.g., /products-ssg/a-line-dress/VA04"
                        .value=${this.path}
                        @input=${this.handlePathInput}
                    ></sp-textfield>
                    <sp-button @click=${this.handleRenderClick} .disabled=${this.isLoading}>Render</sp-button>
                </div>
                
                <sp-progress-circle .indeterminate=${this.isLoading} label="Rendering..." class="${this.isLoading ? '' : 'loading-hidden'}"></sp-progress-circle>

                ${this.isLoading 
                    ? '' 
                    : this.renderedMarkup
                        ? html`
                            <sp-tabs selected="${this.selectedSubTab}" @change="${this.handleSubTabChange}">
                                <sp-tab label="Rendered Markup" value="1"></sp-tab>
                                <sp-tab label="Meta Tags" value="2"></sp-tab>
                                <sp-tab label="JSON-LD Data" value="3"></sp-tab>

                                <sp-tab-panel value="1">
                                    <div class="code-container">
                                        <pre><code class="language-html">${unsafeHTML(this.getHighlightedCode(this.renderedMarkup, 'html'))}</code></pre>
                                    </div>
                                </sp-tab-panel>
                                <sp-tab-panel value="2">
                                    <div class="meta-table-container">
                                        ${this.renderMetaTable()}
                                    </div>
                                </sp-tab-panel>
                                <sp-tab-panel value="3">
                                    <div class="code-container">
                                        <pre><code class="language-json">${unsafeHTML(this.getHighlightedCode(JSON.stringify(this.jsonLdData, null, 2), 'json'))}</code></pre>
                                    </div>
                                </sp-tab-panel>
                            </sp-tabs>
                        `
                        : ''
                }
            </div>
        `;
    }
}

customElements.define('renderer-tab', RendererTab); 